import type { InstagramMedia, MediaInsights } from '../../../shared/api/reports.api';
import type { PaidStats } from '../api/ads-paid-stats';

/**
 * Строка «Статистики аккаунта», собранная из медиа и его метрик.
 *
 * Вынесено из AccountsStatsPage без изменения формул: теперь строку считают два
 * пути — прежний браузерный сбор и серверный /api/reports/collect, — и они
 * обязаны сходиться в каждой цифре. Любая правка формулы здесь автоматически
 * одинаково меняет оба пути.
 */
export interface AccountStatRow {
  mediaId: string;
  thumbnailUrl: string;
  caption: string;
  permalink: string | undefined;
  timestamp: string;
  mediaType: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  saved: number;
  reach: number;
  crossposted_views: number;
  facebook_views: number;
  er: number;
  /**
   * Платная часть, подмешанная после сбора. undefined — разбивка не
   * запрашивалась, null — у поста рекламы не было. Формулы выше её не
   * учитывают: ER и суммы остаются органическими, чтобы оба пути сбора
   * по-прежнему сходились цифра в цифру.
   */
  paid?: PaidStats | null;
}

export function statRowFromMedia(
  media: InstagramMedia,
  insights: Partial<MediaInsights>,
): AccountStatRow {
  const views = insights.views || insights.reach || 0;
  const likes = media.like_count ?? 0;
  const comments = media.comments_count ?? 0;
  const shares = insights.shares || 0;
  const saved = insights.saved || 0;
  const reach = insights.reach || 0;

  // ER = (likes + comments + shares + saved) / views * 100
  const denominator = views || reach;
  const engagement = likes + comments + shares + saved;
  const er = denominator > 0 ? (engagement / denominator) * 100 : 0;

  return {
    mediaId: media.id,
    thumbnailUrl: media.thumbnail_url || media.media_url || '',
    caption: media.caption || '',
    permalink: media.permalink,
    timestamp: media.timestamp,
    mediaType: media.media_type,
    views,
    likes,
    comments,
    shares,
    saved,
    reach,
    crossposted_views: insights.crossposted_views || 0,
    facebook_views: insights.facebook_views || 0,
    er,
  };
}
